import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// react component for creating dynamic tables
import ReactTable from "react-table";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
// @material-ui/icons
import VisibilityIcon from "@material-ui/icons/Visibility";
import Assignment from "@material-ui/icons/Assignment";
import CancelIcon from "@material-ui/icons/Cancel";
// core components
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import { orderApi } from "functions/apis";
import { formatCurrency, formatDateTime } from "functions/Helper";

import { GetOrderStatusDescription } from "enums/OrderStatus";
import { Input } from "@material-ui/core";
import AgentDialog from "components/AgentDialog/AgentDialog";
import { token } from "functions/Helper";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px"
  }
};

const useStyles = makeStyles(styles);

export default function OrderList() {
  const source = useRef(new AbortController());

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  const [showModal, setShowModal] = React.useState(false);
  const [agentName, setAgentName] = React.useState(null);
  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [filter, setFilter] = React.useState({
    searchString: null,
    status: 4,
    agentID: null,
    voucherTypeID: null,
    minTransactionTime: null,
    maxTransactionTime: null,
    minCancelledAt: null,
    maxCancelledTime: null,
    minPaymentProcessedAt: null,
    maxPaymentProcessedAt: null,
    sort: 13,
    isCancelled: false
  });
  const [loading, setLoading] = React.useState(false);
  useEffect(() => {
    setLoading(true);
    orderApi
      .get("/Orders", {
        signal: source.current.signal,
        params: {
          filter: filter,
          pagination: pagination
        }
      })
      .then(data => {
        setData(
          data.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  {(token["order.orders.recalculate_gst"] === "allowed" ||
                    token.is_admin === "True") && (
                    <Link to={"/admin/Orders/RecalculateGST/" + record.id}>
                      <Button color="info" className="like">
                        <VisibilityIcon /> Recalculate GST
                      </Button>
                    </Link>
                  )}
                  <Link to={"/admin/Orders/Detail/" + record.id}>
                    <Button color="info" className="like">
                      <VisibilityIcon /> View
                    </Button>
                  </Link>
                  {(token["order.orders.cancel"] === "allowed" ||
                    token.is_admin === "True") && (
                    <Link to={"/admin/Orders/Cancel/" + record.id}>
                      <Button color="danger" className="remove">
                        <CancelIcon /> Cancel
                      </Button>
                    </Link>
                  )}
                </div>
              )
            };
          })
        );
        setPages(Math.ceil(data.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(error => console.log(error));
  }, [pagination, filter]);

  const handleAgentIDChanged = e => {
    setAgentName(e.name);
    setShowModal(false);
    setFilter({
      ...filter,
      agentID: e.id === null ? "" : e.id
    });
  };

  const handleSearchStringChanged = e =>
    setFilter({
      ...filter,
      searchString: e.target.value === "" ? null : e.target.value
    });

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>ORDER</h4>
          </CardHeader>
          <CardBody>
            <GridItem xs={3}>
              <CustomInput
                labelText="Search"
                formControlProps={{
                  fullWidth: true
                }}
                inputProps={{
                  onChange: handleSearchStringChanged,
                  value: filter.searchString === null ? "" : filter.searchString
                }}
              />
            </GridItem>
            <GridItem xs={3}>
              <FormControl fullWidth className={classes.selectFormControl}>
                <InputLabel htmlFor="selAgent" className={classes.selectLabel}>
                  Agent
                </InputLabel>
                <Input
                  onClick={() => setShowModal(prev => !prev)}
                  className={classes.select}
                  value={agentName ?? ""}
                  disabled
                />
                <AgentDialog
                  openModal={showModal}
                  setOpenModal={val => setShowModal(val)}
                  onSelect={handleAgentIDChanged}
                />
              </FormControl>
            </GridItem>
            {(token["order.orders.bulk_recalculate_gst"] === "allowed" ||
              token.is_admin === "True") && (
              <Link to={"/admin/Orders/BulkRecalculateGST"}>
                <Button color="info" className="like">
                  Bulk Recalculate GST
                </Button>
              </Link>
            )}

            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  Header: "No",
                  accessor: "no"
                },
                {
                  id: "transactionTime",
                  Header: "TransDate",
                  accessor: d =>
                    formatDateTime(new Date(d.transactionTime + "Z"))
                },
                {
                  id: "status",
                  Header: "Status",
                  accessor: d => GetOrderStatusDescription(d.status)
                },
                {
                  id: "agent",
                  Header: "Agent",
                  accessor: d => d.agent.name
                },
                {
                  id: "voucherType",
                  Header: "Voucher Type",
                  accessor: d => d.voucherType.name
                },
                {
                  Header: "Quantity",
                  accessor: "quantity"
                },
                {
                  id: "unitPrice",
                  Header: "Unit Price",
                  accessor: d =>
                    formatCurrency(
                      d.agent.billingCurrency === null
                        ? "XXX"
                        : d.agent.billingCurrency.code,
                      d.unitPrice
                    )
                },
                {
                  id: "total",
                  Header: "Total",
                  accessor: d =>
                    formatCurrency(
                      d.agent.billingCurrency === null
                        ? "XXX"
                        : d.agent.billingCurrency.code,
                      d.total
                    )
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
